import "./Portfolio.css";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import PortfolioNavbar from "../portfolio-components/PortfolioNavbar";
import { Helmet } from "react-helmet";
import { useTransactions } from "../../lib/useTransactions";
import { incomeAmounts, incomeByYear, incomeTransactions } from "../../lib/portfolioMetrics";
import { cachedFetch, TTL } from "../../lib/clientCache";

const TITLE = "Dividends";

function fmtDate(tsMs) {
  if (!tsMs) return "-";
  return new Date(tsMs).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtNum(val, decimals = 2) {
  if (val === null || val === undefined) return "-";
  return Number(val).toFixed(decimals);
}

function DividendView() {
  // Received dividends come from the imported broker transactions — the
  // authoritative record of what was actually paid out.
  const { transactions, loading: txLoading, error: txError } = useTransactions();

  // Upcoming dividends are projected from Yahoo Finance ex-date data.
  const [dividendData, setDividendData] = useState(null);

  const fetchUpcoming = useCallback(async () => {
    try {
      const fns = getFunctions();

      const portfolio = await cachedFetch("portfolio", TTL.PORTFOLIO, async () => {
        const res = await httpsCallable(fns, "getPortfolio")();
        return JSON.parse(res.data);
      });

      const tickerObjects = (Object.entries(portfolio) || []).map(([, v]) => {
        const ticker = Object.keys(v)[0];
        return { ticker, exchange: v[ticker]?.exchange || "" };
      });

      if (tickerObjects.length === 0) {
        setDividendData({});
        return;
      }

      const divs = await cachedFetch("dividends", TTL.DIVIDENDS, async () => {
        const res = await httpsCallable(fns, "getDividendHistory")({ tickers: tickerObjects });
        return JSON.parse(res.data);
      });
      setDividendData(divs);
    } catch (err) {
      console.error("DividendView upcoming fetch error:", err);
      setDividendData({});
    }
  }, []);

  useEffect(() => { fetchUpcoming(); }, [fetchUpcoming]);

  // All ticker-linked income (dividends, special dividends, REIT property
  // income distributions); account-level interest is excluded from this view.
  const received = useMemo(
    () => incomeTransactions(transactions).filter((tx) => tx.ticker),
    [transactions]
  );
  const annualRows = useMemo(() => incomeByYear(transactions), [transactions]);

  const totalNet = received.reduce(
    (sum, tx) => sum + (incomeAmounts(tx).net ?? 0), 0
  );

  const upcoming = dividendData
    ? Object.entries(dividendData).flatMap(([ticker, d]) =>
        (d?.exDates ?? []).filter((e) => e.isFuture).map((e) => ({ ...e, ticker }))
      ).sort((a, b) => a.exDate - b.exDate)
    : [];

  return (
    <div className="DividendView">
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <div className="row">
        <div className="sidebar col-md-auto">
          <PortfolioNavbar />
        </div>

        <div className="main-content col">
          <h2>Dividend Breakdown</h2>

          {txLoading && <p>Loading dividend history...</p>}
          {txError   && <p className="text-danger">Error: {txError.message}</p>}

          {!txLoading && !txError && (
            <>
              {annualRows.length > 0 && (
                <>
                  <h5>Income by Year</h5>
                  <table className="table table-sm" style={{ maxWidth: 480 }}>
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Payments</th>
                        <th>Gross</th>
                        <th>Tax Withheld</th>
                        <th>Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {annualRows.map((r) => (
                        <tr key={r.year}>
                          <td>{r.year}</td>
                          <td>{r.count}</td>
                          <td>{fmtNum(r.gross)}</td>
                          <td>{fmtNum(r.taxWithheld)}</td>
                          <td className="text-success">{fmtNum(r.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <h5>Received</h5>
              <p className="text-muted">
                Total net income: <strong>{fmtNum(totalNet)}</strong> (account currency)
              </p>
              {received.length === 0 ? (
                <p className="text-muted">No dividend history found. Import a Freetrade CSV to get started.</p>
              ) : (
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Ticker</th>
                      <th>Pay Date</th>
                      <th>Ex-Date</th>
                      <th>Eligible Shares</th>
                      <th>Per Share</th>
                      <th>Gross</th>
                      <th>Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {received.map((tx, i) => {
                      const { gross, net } = incomeAmounts(tx);
                      return (
                        <tr key={i}>
                          <td>{tx.ticker || "-"}</td>
                          <td>{fmtDate(tx.dividendPayDate || tx.timestamp)}</td>
                          <td>{fmtDate(tx.dividendExDate)}</td>
                          <td>{fmtNum(tx.dividendEligibleQuantity, 4)}</td>
                          <td>{fmtNum(tx.dividendAmountPerShare, 4)}</td>
                          <td>{fmtNum(gross)}</td>
                          <td>{fmtNum(net)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <h5 className="mt-4">Upcoming (projected)</h5>
              {upcoming.length === 0 ? (
                <p className="text-muted">No upcoming ex-dates found.</p>
              ) : (
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Ticker</th>
                      <th>Ex-Date</th>
                      <th>Shares Held</th>
                      <th>Per Share</th>
                      <th>Est. Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcoming.map((e) => (
                      <tr key={`${e.ticker}-${e.exDate}`} className="fst-italic text-muted">
                        <td>{e.ticker}</td>
                        <td>{fmtDate(e.exDate)}</td>
                        <td>{fmtNum(e.sharesHeld, 4)}</td>
                        <td>{fmtNum(e.perShare, 4)}</td>
                        <td>{fmtNum(e.totalGross, 4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DividendView;
